/**
 * @fileoverview Generic dashboard layout component
 * This component provides a consistent structure for all profile dashboards
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Skeleton } from "@mui/material";
import { DmsPage, DmsAltBox, intl, fetchData } from "dmsbasereact";
import { Grid } from "@mui/material";
import { getUserTypeLabel, mapBackendTypeToFrontend } from "../../constants/UserType";

import { DashboardSection } from "./DashboardSection";
import { DashboardStatsGrid } from "./DashboardStatsGrid";
import { DashboardQuickAccessGrid } from "./DashboardQuickAccessGrid";
import { DashboardAlertsGrid } from "./DashboardAlertsGrid";

/**
 * Loading skeleton for dashboard
 */
const DashboardSkeleton = () => (
    <>
        <Box sx={{ mb: 4 }}>
            <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
            <Grid container spacing={2}>
                {[1, 2, 3, 4].map((i) => (
                    <Grid item xs={6} sm={6} md={3} key={i}>
                        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
                    </Grid>
                ))}
            </Grid>
        </Box>
        <Box sx={{ mb: 4 }}>
            <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 3 }}>
                {[1, 2, 3, 4].map((i) => (
                    <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Skeleton variant="rounded" width={80} height={80} sx={{ borderRadius: 3 }} />
                        <Skeleton variant="text" width={70} />
                    </Box>
                ))}
            </Box>
        </Box>
        <Box>
            <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
            <Grid container spacing={3}>
                {[1, 2].map((i) => (
                    <Grid item xs={12} md={6} key={i}>
                        <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    </>
);

/**
 * DashboardLayout - Main dashboard layout component
 *
 * @param {Object} props
 * @param {string} props.welcomeName - Name to display in welcome message
 * @param {boolean} props.loading - Whether data is loading
 * @param {Array} props.stats - Stats configuration for DashboardStatsGrid
 * @param {Array} props.quickAccess - Quick access buttons configuration
 * @param {Array} props.alerts - Alerts configuration for DashboardAlertsGrid
 * @param {string} props.statsTitle - Title for stats section
 * @param {string} props.quickAccessTitle - Title for quick access section
 * @param {string} props.alertsTitle - Title for alerts section
 */
export const DashboardLayout = ({
    welcomeName,
    loading = false,
    stats = [],
    quickAccess = [],
    alerts = [],
    statsTitle,
    quickAccessTitle,
    alertsTitle,
    children
}) => {
    const [userTypeValues, setUserTypeValues] = useState([]);

    useEffect(() => {
        const fetchUserType = async () => {
            try {
                const response = await fetchData('/person/my_profile', 'GET', true);
                if (response?.data.data?.user_types) {
                    // Filter out "person" and "regular_user" as they are not meaningful for display
                    // Also filter out "institution_responsible" if "distributor_responsible" is present
                    const types = response.data.data.user_types;
                    const hasDistributor = types.includes("distributor_responsible");

                    const displayableTypes = types
                        .filter(t => t !== "person" && t !== "regular_user")
                        .filter(t => !(hasDistributor && t === "institution_responsible"))
                        .map(t => mapBackendTypeToFrontend(t));
                    setUserTypeValues(displayableTypes);
                }
            } catch (error) {
                // Keep default empty array on error
            }
        };
        fetchUserType();
    }, []);

    // Get translated labels at render time with bullet separator
    const userProfile = userTypeValues.length > 0
        ? userTypeValues.map(t => getUserTypeLabel(t)).join(" • ")
        : "-";

    const defaultStatsTitle = intl.get("Overview").d("Overview");
    const defaultQuickAccessTitle = intl.get("QuickAccess").d("Quick Access");
    const defaultAlertsTitle = intl.get("AlertsAndNotifications").d("Alerts & Notifications");

    const headerText = welcomeName
        ? `${intl.get("Welcome").d("Welcome")}, ${welcomeName}`
        : intl.get("Dashboard").d("Dashboard");

    return (
        <DmsPage name='Dashboard'>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant='h3' sx={{ fontSize: '1.1rem' }}>
                        {headerText}
                    </Typography>
                    <DmsAltBox.HeaderRight>
                        <Typography variant='h3' sx={{ fontSize: '1.1rem' }}>
                            {intl.get("UserType").d("User Type")}: {userProfile}
                        </Typography>
                    </DmsAltBox.HeaderRight>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <Box sx={{ py: { xs: 1, md: 2 } }}>
                        {loading ? (
                            <DashboardSkeleton />
                        ) : (
                            <>
                                {/* Stats Section */}
                                {stats && stats.length > 0 && (
                                    <DashboardSection title={statsTitle || defaultStatsTitle}>
                                        <DashboardStatsGrid stats={stats} />
                                    </DashboardSection>
                                )}

                                {/* Quick Access Section */}
                                {quickAccess && quickAccess.length > 0 && (
                                    <DashboardSection title={quickAccessTitle || defaultQuickAccessTitle}>
                                        <DashboardQuickAccessGrid buttons={quickAccess} />
                                    </DashboardSection>
                                )}

                                {/* Alerts Section */}
                                {alerts && alerts.length > 0 && (
                                    <DashboardSection title={alertsTitle || defaultAlertsTitle}>
                                        <DashboardAlertsGrid alerts={alerts} />
                                    </DashboardSection>
                                )}

                                {/* Custom children content */}
                                {children}
                            </>
                        )}
                    </Box>
                </DmsAltBox.Body>
            </DmsAltBox.Card>
        </DmsPage>
    );
};

DashboardLayout.propTypes = {
    welcomeName: PropTypes.string,
    loading: PropTypes.bool,
    stats: PropTypes.array,
    quickAccess: PropTypes.array,
    alerts: PropTypes.array,
    statsTitle: PropTypes.string,
    quickAccessTitle: PropTypes.string,
    alertsTitle: PropTypes.string,
    children: PropTypes.node
};
